import React, { useEffect, useState } from "react";
import { Save, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AdminHeader } from "@/components/AdminHeader";

const TYPE_PILL = { holiday: "bg-ink text-gold", celebration: "bg-gold text-ink", recognition: "bg-paper text-ink border border-ink" };

const Row = ({ o, types, onSaved }) => {
  const [form, setForm] = useState({ name: o.name, details: o.details, type: o.type });
  const [saving, setSaving] = useState(false);
  const dirty = form.name !== o.name || form.details !== o.details || form.type !== o.type;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/observances/${o.date_dd_mm}`, form);
      onSaved(r.data);
      toast.success(`${o.date_dd_mm} saved`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr data-testid={`obs-row-${o.date_dd_mm}`} className="border-b border-slate-100 align-top">
      <td className="py-3 pr-3 font-mono text-xs text-ink-muted whitespace-nowrap">{o.date_dd_mm}{o.edited && <span className="ml-1 text-gold" title="Edited">•</span>}</td>
      <td className="py-3 pr-3">
        <select data-testid={`obs-type-${o.date_dd_mm}`} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${TYPE_PILL[form.type]}`}>
          {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </td>
      <td className="py-3 pr-3 w-64">
        <input data-testid={`obs-name-${o.date_dd_mm}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-ink focus:border-gold outline-none" />
      </td>
      <td className="py-3 pr-3">
        <textarea data-testid={`obs-details-${o.date_dd_mm}`} rows={2} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-ink focus:border-gold outline-none resize-y" />
      </td>
      <td className="py-3">
        <button data-testid={`obs-save-${o.date_dd_mm}`} onClick={save} disabled={!dirty || saving} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-ink text-white font-mono text-xs disabled:opacity-30 transition-opacity">
          <Save className="w-3.5 h-3.5" /> {saving ? "…" : "Save"}
        </button>
      </td>
    </tr>
  );
};

export default function AdminObservances() {
  const [data, setData] = useState({ items: [], counts: {}, types: {} });
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/observances", { params: { q, type } }).then((r) => setData(r.data)).catch(() => toast.error("Could not load observances")).finally(() => setLoading(false));
  };
  useEffect(load, [q, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSaved = (row) => setData((d) => ({ ...d, items: d.items.map((o) => (o.date_dd_mm === row.date_dd_mm ? row : o)) }));

  return (
    <div className="min-h-screen bg-paper">
      <AdminHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-observances-page">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Observances</p>
        <h1 className="mt-1 text-3xl font-bold text-ink">Holidays, celebrations & days of recognition</h1>
        <p className="mt-2 text-sm text-ink-muted">One entry per calendar day (366). Edit the type, name or details — changes go live on the homepage, daily email and public feed immediately.</p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button data-testid="obs-filter-all" onClick={() => setType("")} className={`px-3 h-8 rounded-full font-mono text-xs ${type === "" ? "bg-ink text-white" : "bg-white border border-slate-200 text-ink-muted"}`}>All 366</button>
          {Object.entries(data.types).map(([k, v]) => (
            <button key={k} data-testid={`obs-filter-${k}`} onClick={() => setType(k)} className={`px-3 h-8 rounded-full font-mono text-xs ${type === k ? "bg-ink text-white" : "bg-white border border-slate-200 text-ink-muted"}`}>
              {v} <span className="opacity-60">{data.counts[k] ?? 0}</span>
            </button>
          ))}
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <input data-testid="obs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, details or DD-MM" className="h-9 w-72 rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm text-ink focus:border-gold outline-none" />
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white px-4">
          <table className="w-full text-left">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-slate-200">
                <th className="py-3 pr-3">Date</th><th className="py-3 pr-3">Type</th><th className="py-3 pr-3">Name</th><th className="py-3 pr-3">Details</th><th className="py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-10 text-center text-sm text-ink-muted">Loading…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={5} className="py-10 text-center text-sm text-ink-muted" data-testid="obs-empty">No observances match.</td></tr>
              ) : data.items.map((o) => <Row key={o.date_dd_mm} o={o} types={data.types} onSaved={onSaved} />)}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
